import { Check, Plus } from 'lucide-react';
import type { Program, MembershipTier } from '@/lib/api';
import { FieldError } from './FieldError';

const ALL_PROGRAMS: Program[] = ['Boxing', 'MuayThai', 'MMA', 'BJJ', 'Kids'];

// Copy is taken directly from dallasmmaboxing.com/programs. Do not embellish.
// Update this constant when the marketing page changes; do not paraphrase.
const PROGRAM_INFO: Record<Program, { label: string; image: string; objectPosition?: string }> = {
  Boxing: {
    label: 'Boxing',
    image: '/assets/instagram/B1Fl9aAHr0S_01.jpg',
    objectPosition: '71% 36%',
  },
  MuayThai: {
    label: 'Muay Thai',
    image: '/assets/instagram/Ch9qaeSOSXx_01.jpg',
    objectPosition: '50% 13%',
  },
  MMA: {
    label: 'MMA',
    image: '/assets/instagram/B4ihm1CHy2d_01.jpg',
    objectPosition: '50% 35%',
  },
  BJJ: {
    label: 'Brazilian Jiu-Jitsu',
    image: '/assets/instagram/CNF_KsWsPEM_01.jpg',
    objectPosition: '31% 78%',
  },
  Kids: {
    label: 'Kids (Ages 8 - 13)',
    image: '/assets/programs/kids.jpg',
  },
};

export function tierFromPrograms(programs: Program[]): MembershipTier | null {
  if (programs.length === 0) return null;
  if (programs.length === 1 && programs[0] === 'Kids') return 'KIDS';
  if (programs.includes('Kids')) return null;
  if (programs.length === 1) return 'ONE_PROGRAM';
  if (programs.length === 2) return 'TWO_PROGRAMS';
  return 'UNLIMITED';
}

export function ProgramsStep(props: {
  programs: Program[];
  setPrograms: (p: Program[]) => void;
  minorParticipant: boolean;
  setMinorParticipant: (minor: boolean) => void;
  /** Server-driven via the QR gate code — not user-editable. */
  athletes: boolean;
  fieldError?: string | null;
  clearFieldError?: () => void;
}) {
  // Kids is mutually exclusive with adult disciplines — pricing tier KIDS is
  // its own SKU. Selecting Kids clears the rest; selecting an adult program
  // while Kids is on clears Kids.
  const toggle = (p: Program) => {
    props.clearFieldError?.();
    if (props.programs.includes(p)) {
      props.setPrograms(props.programs.filter((x) => x !== p));
      return;
    }
    if (p === 'Kids') {
      props.setMinorParticipant(false);
      props.setPrograms(['Kids']);
      return;
    }
    const others = props.programs.filter((x) => x !== 'Kids');
    props.setPrograms([...others, p]);
  };

  return (
    <div className="grid gap-4">
      <fieldset id="field-programs" tabIndex={-1}>
        <legend className="mb-2 text-xs uppercase tracking-wider text-bone/60">Programs</legend>
        <div className="grid gap-3">
          {ALL_PROGRAMS.map((p) => {
            const info = PROGRAM_INFO[p];
            const checked = props.programs.includes(p);
            return (
              <label
                key={p}
                className={`group relative grid cursor-pointer grid-cols-[5.5rem_1fr_auto] items-center overflow-hidden rounded-lg border bg-neutral-900 transition-colors sm:grid-cols-[6rem_1fr_auto] ${
                  checked
                    ? 'border-accent bg-accent/10'
                    : 'border-bone/15 bg-neutral-900 hover:border-bone/30'
                }`}
              >
                <input
                  type="checkbox"
                  aria-label={info.label}
                  className="absolute inset-0 z-10 cursor-pointer opacity-0"
                  checked={checked}
                  onChange={() => toggle(p)}
                />
                <div className="aspect-square overflow-hidden bg-neutral-800">
                  <img
                    src={info.image}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    style={{ objectPosition: info.objectPosition }}
                  />
                </div>
                <div className="min-w-0 px-4">
                  <span className="font-display text-xl leading-tight">{info.label}</span>
                </div>
                <span
                  aria-hidden
                  className={`mr-4 grid h-8 w-8 place-items-center rounded-full border ${
                    checked ? 'border-accent bg-accent text-ink' : 'border-bone/30 text-bone/60'
                  }`}
                >
                  {checked ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </span>
              </label>
            );
          })}
        </div>
        <FieldError message={props.fieldError} />
      </fieldset>
      {!props.programs.includes('Kids') && (
        <label className="flex items-start gap-3 rounded-lg border border-bone/15 bg-neutral-900/40 p-3 text-sm text-bone/80">
          <input
            type="checkbox"
            checked={props.minorParticipant}
            onChange={(e) => props.setMinorParticipant(e.target.checked)}
            className="mt-1"
          />
          <span>Member is under 18</span>
        </label>
      )}
    </div>
  );
}
